import { Brand, Market, PaymentType, prettyPrintBrand } from '../context/claims';
import { env } from '../util/env';
import { realIframe } from '../../platform/iframe/realIframe';
import { getUuidFromJwt } from '../util/jwt';
import { loadMixpanel, emptyMixpanelClient, type MixpanelClient } from './mixpanelLoader';
import {
  errorEvents,
  outcomeEvents,
  type ViewEventName,
  type InteractionEventName,
} from './mixpanelEvents';
import { type RedirectReason } from '../api/redirect';
import { type OpenInAppVariant } from '../util/openInAppVariant';
import { getViewContext } from '../util/viewContext';

// Whether v2 will track this payer. Derived from claims alone so the open-in-app rollout can gate
// on it while flags resolve, before the tracker itself has been initialised.
export function willTrack(source: { shouldTrackMixpanel: boolean } | null): boolean {
  return !!source?.shouldTrackMixpanel && !!env.VITE_MIXPANEL_TOKEN_V2;
}

export interface TrackingContext {
  brand: Brand;
  market?: Market;
  paymentType?: PaymentType;
  shouldShowFancyDeeplinkButton: boolean;
  openInAppVariant?: OpenInAppVariant;
  hasAttemptedRedirect: boolean;
  shouldTrackMixpanel: boolean;
  showNewDesign: boolean;
}

const MIXPANEL_INIT_CONFIG = {
  api_host: 'https://api-eu.mixpanel.com',
  track_pageview: false,
  autocapture: false,
  store_google: false,
  save_referrer: false,
  secure_cookie: true,
  cross_subdomain_cookie: false,
  persistence: 'localStorage' as const,
  disable_persistence: true,
  batch_requests: false,
};

function readSessionIdFromUrl(): string | undefined {
  try {
    const token = new URLSearchParams(window.location.search).get('token');
    return token ? getUuidFromJwt(token) : undefined;
  } catch {
    return undefined;
  }
}

export class MixpanelService {
  private sessionId = readSessionIdFromUrl();
  private client: MixpanelClient = emptyMixpanelClient;
  private enabled = false;

  constructor() {
    loadMixpanel().then((client) => {
      this.client = client;
    });
  }

  private enable(context: TrackingContext): boolean {
    if (this.enabled) return true;

    const token = env.VITE_MIXPANEL_TOKEN_V2;
    if (!token || !willTrack(context) || !this.sessionId || !this.client.init) {
      return false;
    }

    try {
      this.client.init(token, MIXPANEL_INIT_CONFIG);
      this.enabled = true;
    } catch (error) {
      console.error('Failed to initialize Mixpanel:', error);
    }

    return this.enabled;
  }

  private track(
    eventName: string,
    props?: Record<string, unknown>,
    options?: { transport?: 'xhr' | 'sendBeacon' }
  ): void {
    if (!this.enabled) return;

    try {
      this.client.track(eventName, props, options);
    } catch (error) {
      console.error('Mixpanel tracking error:', error);
    }
  }

  public setTrackingContext(context: TrackingContext): void {
    if (!this.enable(context)) return;

    this.client.register({
      Brand: prettyPrintBrand(context.brand),
      'Merchant Market': context.market,
      'Payment Type': context.paymentType,
      'Open In App Shown': context.shouldShowFancyDeeplinkButton,
      'Open In App Variant': context.openInAppVariant,
      'Has Attempted Redirect': context.hasAttemptedRedirect,
      Design: context.showNewDesign ? 'Redesign' : 'Legacy',
      'View Context': getViewContext(),
      'Widget Embed': realIframe.isWidgetSdkEmbed(),
      ePaymentId: this.sessionId,
      $device_id: this.sessionId,
      user_type: 'user',
    });
  }

  public setMerchantSerialNumber(msn?: string): void {
    if (!this.enabled || !msn) return;

    this.client.register({ 'Merchant Serial Number': msn });
  }

  public trackViewLoad(
    viewEventName: ViewEventName,
    additionalProps?: Record<string, unknown>
  ): void {
    this.track(viewEventName, additionalProps);
  }

  public trackInteraction(
    action: InteractionEventName,
    additionalProps?: Record<string, unknown>
  ): void {
    this.track(action, additionalProps);
  }

  public trackOutcome(reason: RedirectReason): void {
    // The page navigates away immediately after this, which would cancel an in-flight XHR.
    this.track(
      outcomeEvents.LANDING_PAGE_REDIRECTED,
      { 'Redirect Reason': reason },
      { transport: 'sendBeacon' }
    );
  }

  public trackError(viewEventName: ViewEventName, error: string): void {
    this.track(errorEvents.ERROR_OCCURRED, {
      'View Name': viewEventName,
      'Error Message': error,
    });
  }
}

export const mixpanelService = new MixpanelService();
